import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Alert,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowLeft,
  FileText,
  Send,
  X,
  CheckCircle2,
  History
} from 'lucide-react';
import DocumentViewer from './DocumentViewer'; // Componente modificado basado en OfficeDocumentEditor
import DocumentSelector from './DocumentSelector'; // Componente para seleccionar documentos
import DocumentHistoryPanel from './DocumentHistoryPanel'; // Componente para ver historial
import agtEduService from '../services/agtEduService';
import ThinkingIcon from '../components/ThinkingIcon';

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// Contenedor principal
const EditorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000'
}));

// Barra de encabezado
const HeaderBar = styled(Box)(({ theme, colors }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${colors.borderColor}`,
  backgroundColor: colors.background
}));

// Área principal con diseño responsive
const MainArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column'
  }
}));

// Sidebar para listado de documentos
const SidebarArea = styled(Box)(({ theme, colors, isMobile, sidebarOpen }) => ({
  width: isMobile ? '100%' : 300,
  borderRight: `1px solid ${colors.borderColor}`,
  backgroundColor: colors.background,
  overflow: 'hidden',
  flexDirection: 'column',
  display: isMobile ? (sidebarOpen ? 'flex' : 'none') : 'flex',
  [theme.breakpoints.down('md')]: {
    borderRight: 'none',
    borderBottom: `1px solid ${colors.borderColor}`,
    maxHeight: sidebarOpen ? 350 : 0,
    transition: theme.transitions.create('max-height', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    })
  }
}));

// Área de contenido principal
const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}));

// Área para el visor de documentos
const DocumentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
}));

// Área para el chat
const ChatArea = styled(Box)(({ theme, colors }) => ({
  borderTop: `1px solid ${colors.borderColor}`,
  padding: theme.spacing(2),
  backgroundColor: colors.backgroundTertiary
}));

// Componente para los mensajes en el chat
const MessageBubble = styled(Box)(({ theme, colors, isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isUser ? 'flex-end' : 'flex-start',
  marginBottom: theme.spacing(2),
}));

const MessageContent = styled(Paper)(({ theme, colors, isUser }) => ({
  padding: theme.spacing(2),
  backgroundColor: isUser 
    ? theme.palette.mode === 'light' ? '#E2E2E7' : colors.cardBackground
    : colors.backgroundSecondary,
  borderRadius: theme.spacing(2),
  maxWidth: '80%'
}));

// Removed unused FileIcon component

const DocumentEditorView = ({ onBack }) => {
  const theme = useTheme();
  const colors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef(null);
  
  // Estado general - use only the setter function for unused state variables
  const [, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  
  // Estado para el chat
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  
  // Estado para los cambios de documento
  const [documentChanges, setDocumentChanges] = useState(null);
  const [, setChangeHistory] = useState([]);
  
  // Estado para las pestañas del sidebar
  const [activeTab, setActiveTab] = useState('documents');
  
  // Cargar documentos al montar el componente
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Hacer scroll cuando se añaden nuevos mensajes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Función para obtener documentos
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agtEduService.getIndividualFiles();
      
      // Filtrar solo documentos editables
      const editableDocuments = response.data.filter(doc => {
        const fileExt = doc.name ? doc.name.split('.').pop().toLowerCase() : '';
        return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'txt'].includes(fileExt);
      });
      
      setDocuments(editableDocuments);
    } catch (err) {
      console.error('Error al cargar documentos:', err);
      setError('No se pudieron cargar los documentos. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Función para seleccionar un documento
  const handleSelectDocument = (document) => {
    setSelectedDocument(document);
    setDocumentChanges(null);
    
    // Limpiar mensajes previos cuando se cambia de documento
    setMessages([]);
    
    // Cerrar sidebar en móvil
    if (isMobile) {
      setSidebarOpen(false);
    }
    
    // Añadir mensaje de sistema
    setMessages([{
      id: 'system-' + Date.now(),
      role: 'system',
      text: `Has seleccionado el documento "${document.name}". Puedes enviar instrucciones sobre qué cambios deseas realizar.`,
      timestamp: new Date().toISOString()
    }]);
  };

  // Función para enviar un mensaje al asistente
  const handleSendMessage = async () => {
    if (!message.trim() || !selectedDocument) return;
    
    try {
      // Mensaje del usuario
      const userMessage = {
        id: 'user-' + Date.now(),
        role: 'user',
        text: message,
        timestamp: new Date().toISOString()
      };
      
      // Actualizar UI con mensaje del usuario
      setMessages(prev => [...prev, userMessage]);
      setMessage(''); // Limpiar input
      
      // Añadir indicador de "pensando"
      setIsThinking(true);
      const thinkingStartTime = Date.now();
      
      // Preparar datos para la petición
      const messageData = {
        text: `Estoy editando el documento "${selectedDocument.name}". ${message}`,
        documentId: selectedDocument.id,
        documentName: selectedDocument.name
      };
      
      // Enviar mensaje al servidor
      const response = await agtEduService.sendMessage(messageData);
      
      // Asegurar que el indicador "pensando" se muestre al menos por 1.5 segundos
      const thinkingElapsed = Date.now() - thinkingStartTime;
      if (thinkingElapsed < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - thinkingElapsed));
      }
      
      // Procesar respuesta
      if (response.success && response.data) {
        // Añadir mensaje del asistente
        const assistantMessage = {
          id: 'assistant-' + Date.now(),
          role: 'assistant',
          text: response.data.message,
          timestamp: new Date().toISOString()
        };
        
        // Actualizar mensajes
        setMessages(prev => [...prev, assistantMessage]);
        
        // Si hay cambios de documento, actualizarlos
        if (response.data.metadata?.documentChanges) {
          setDocumentChanges(response.data.metadata.documentChanges);
          
          // Añadir a historial de cambios
          setChangeHistory(prev => [
            ...prev,
            {
              id: 'change-' + Date.now(),
              changes: response.data.metadata.documentChanges,
              message: userMessage.text,
              timestamp: new Date().toISOString()
            }
          ]);
        }
      } else {
        throw new Error('No se pudo obtener respuesta del asistente');
      }
    } catch (err) {
      console.error('Error al enviar mensaje:', err);
      
      // Añadir mensaje de error
      setMessages(prev => [
        ...prev,
        {
          id: 'error-' + Date.now(),
          role: 'error',
          text: 'No se pudo procesar tu solicitud. Por favor, inténtalo de nuevo.',
          timestamp: new Date().toISOString()
        }
      ]);
      
      setError('No se pudo procesar tu solicitud. Por favor, inténtalo de nuevo.');
    } finally {
      setIsThinking(false);
    }
  };

  // Función para aplicar cambios al documento
  const handleApplyChanges = async () => {
    if (!documentChanges || !selectedDocument) return;
    
    try {
      setLoading(true);
      
      // Aquí iría la lógica para aplicar los cambios al documento
      // Por ejemplo:
      // await agtEduService.applyDocumentChanges(selectedDocument.id, documentChanges);
      
      // Mensaje de éxito
      setMessages(prev => [
        ...prev,
        {
          id: 'system-' + Date.now(),
          role: 'system',
          text: 'Los cambios han sido aplicados al documento correctamente.',
          timestamp: new Date().toISOString()
        }
      ]);
      
      // Resetear cambios actuales
      setDocumentChanges(null);
    } catch (err) {
      console.error('Error al aplicar cambios:', err);
      setError('No se pudieron aplicar los cambios al documento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditorContainer>
      {/* Barra de encabezado */}
      <HeaderBar colors={colors}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={onBack}
            sx={{ mr: 2, color: colors.textDefault }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 500, color: colors.textDefault }}>
            Editor de Documentos
          </Typography>
        </Box>
        
        {isMobile && selectedDocument && (
          <Button
            variant="outlined"
            onClick={() => setSidebarOpen(prev => !prev)}
            startIcon={sidebarOpen ? <X size={16} /> : <FileText size={16} />}
            size="small"
            sx={{
              color: colors.textDefault,
              borderColor: colors.borderColor
            }}
          >
            {sidebarOpen ? 'Cerrar' : 'Documentos'}
          </Button>
        )}
      </HeaderBar>
      
      {/* Área principal */}
      <MainArea>
        {/* Sidebar con lista de documentos */}
        <SidebarArea 
          colors={colors} 
          isMobile={isMobile} 
          sidebarOpen={sidebarOpen}
        >
          {/* Pestañas del sidebar */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ 
              borderBottom: `1px solid ${colors.borderColor}`,
              minHeight: 48
            }}
          >
            <Tab 
              value="documents" 
              label="Documentos" 
              icon={<FileText size={16} />}
              iconPosition="start"
              sx={{ 
                minHeight: 48,
                textTransform: 'none',
                color: activeTab === 'documents' ? colors.accent : colors.textSecondary
              }}
            />
            <Tab 
              value="history" 
              label="Historial" 
              icon={<History size={16} />}
              iconPosition="start"
              sx={{ 
                minHeight: 48,
                textTransform: 'none',
                color: activeTab === 'history' ? colors.accent : colors.textSecondary
              }}
            />
          </Tabs>
          
          {/* Área de contenido del sidebar */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {activeTab === 'documents' ? (
              <DocumentSelector 
                onSelectDocument={handleSelectDocument}
                selectedDocument={selectedDocument}
                colors={colors}
              />
            ) : (
              <Box sx={{ p: 2 }}>
                <DocumentHistoryPanel 
                  documentId={selectedDocument?.id}
                  colors={colors}
                />
              </Box>
            )}
          </Box>
        </SidebarArea>
        
        {/* Área de contenido principal */}
        <ContentArea>
          {selectedDocument ? (
            <>
              {/* Área del documento */}
              <DocumentArea>
                <DocumentViewer
                  file={selectedDocument}
                  changes={documentChanges}
                  colors={colors}
                  onApplyChanges={handleApplyChanges}
                />
              </DocumentArea>
              
              {/* Área de chat */}
              <ChatArea colors={colors}>
                {/* Área de mensajes */}
                {messages.length > 0 && (
                  <Box 
                    sx={{ 
                      maxHeight: 200, 
                      overflowY: 'auto', 
                      mb: 2,
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: colors.backgroundSecondary
                    }}
                  >
                    {messages.map(msg => (
                      <MessageBubble
                        key={msg.id}
                        colors={colors}
                        isUser={msg.role === 'user'}
                      >
                        {msg.role === 'system' ? (
                          <Alert 
                            severity="info" 
                            sx={{ 
                              mb: 2, 
                              width: '100%',
                              '& .MuiAlert-message': {
                                color: colors.textDefault
                              }
                            }}
                          >
                            {msg.text}
                          </Alert>
                        ) : (
                          <MessageContent 
                            colors={colors} 
                            isUser={msg.role === 'user'}
                            elevation={0}
                          >
                            <Typography sx={{ color: colors.textDefault }}>
                              {msg.text}
                            </Typography>
                          </MessageContent>
                        )}
                      </MessageBubble>
                    ))}
                    
                    {isThinking && (
                      <Box sx={{ width: '100%', mb: 2 }}>
                        <ThinkingIcon colors={colors} startTime={Date.now()} />
                      </Box>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </Box>
                )}
                
                {/* Input de mensaje */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Describe qué cambios quieres hacer en el documento..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isThinking || loading}
                    multiline
                    maxRows={4}
                    InputProps={{
                      sx: {
                        backgroundColor: colors.inputBackground,
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'transparent'
                        },
                        '&:hover fieldset': {
                          borderColor: colors.borderColor
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.accent + ' !important',
                          borderWidth: '1px !important'
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    disabled={!message.trim() || isThinking || loading || !selectedDocument}
                    onClick={handleSendMessage}
                    endIcon={<Send size={16} />}
                    sx={{
                      backgroundColor: colors.accent,
                      color: '#FFF',
                      borderRadius: 2,
                      height: 56,
                      minWidth: 56,
                      '&:hover': {
                        backgroundColor: colors.accent
                      }
                    }}
                  >
                    {isMobile ? '' : 'Enviar'}
                  </Button>
                </Box>
                
                {/* Mensaje para documento no seleccionado */}
                {!selectedDocument && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Selecciona un documento para comenzar a editarlo
                  </Alert>
                )}
                
                {/* Mensaje para cambios disponibles */}
                {documentChanges && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box>
                      Se han detectado {documentChanges.changes?.length || 0} cambios para aplicar al documento
                    </Box>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={handleApplyChanges}
                      startIcon={<CheckCircle2 size={16} />}
                    >
                      Aplicar Cambios
                    </Button>
                  </Alert>
                )}
              </ChatArea>
            </>
          ) : (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                flexDirection: 'column',
                p: 3
              }}
            >
              <FileText size={64} color={colors.textSecondary} style={{ opacity: 0.5 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  mt: 2, 
                  color: colors.textSecondary,
                  textAlign: 'center'
                }}
              >
                Selecciona un documento para empezar a editar
              </Typography>
              <Typography 
                variant="body2"
                sx={{ 
                  mt: 1, 
                  color: colors.textSecondary,
                  textAlign: 'center',
                  maxWidth: 400
                }}
              >
                Puedes editar documentos Word, Excel, PowerPoint, PDF y archivos de texto
              </Typography>
              
              {isMobile && !sidebarOpen && (
                <Button
                  variant="outlined"
                  onClick={() => setSidebarOpen(true)}
                  startIcon={<FileText size={16} />}
                  sx={{ mt: 3 }}
                >
                  Ver mis documentos
                </Button>
              )}
            </Box>
          )}
        </ContentArea>
      </MainArea>
    </EditorContainer>
  );
};

export default DocumentEditorView;